import { debounce } from '@theme/utilities';

const RESIZE_DEBOUNCE_MS = 150;

/**
 * @param {HTMLElement} marquee
 * @returns {Record<string, string> | null}
 */
function getAnnouncementTextStyles(marquee) {
  const slide = marquee.closest('.text-block');
  const template = marquee.querySelector('.announcement-marquee__segment');
  const source = slide instanceof HTMLElement ? slide : template;

  if (!(source instanceof HTMLElement)) return null;

  const computed = getComputedStyle(source);

  return {
    fontSize: computed.fontSize,
    fontFamily: computed.fontFamily,
    lineHeight: computed.lineHeight,
    fontWeight: computed.fontWeight,
    letterSpacing: computed.letterSpacing,
  };
}

/**
 * @param {HTMLElement} element
 * @param {Record<string, string> | null} styles
 */
function applyAnnouncementTextStyles(element, styles) {
  if (!styles) return;

  element.style.fontSize = styles.fontSize;
  element.style.fontFamily = styles.fontFamily;
  element.style.lineHeight = styles.lineHeight;
  element.style.fontWeight = styles.fontWeight;
  element.style.letterSpacing = styles.letterSpacing;
}

/**
 * @param {string} gapValue
 * @returns {number}
 */
function parseGapToPx(gapValue) {
  if (!gapValue) return 48;

  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const value = parseFloat(gapValue);

  if (Number.isNaN(value)) return 48;
  if (gapValue.includes('rem')) return value * rootFontSize;
  return value;
}

/**
 * @param {HTMLElement} marquee
 */
function buildAnnouncementMarquee(marquee) {
  const track = marquee.querySelector('.announcement-marquee__track');
  const template = marquee.querySelector('.announcement-marquee__segment');

  if (!track || !template) return;

  const segmentClass = template.className;
  const segmentHTML = template.innerHTML;
  const textStyles = getAnnouncementTextStyles(marquee);
  const gapPx = parseGapToPx(getComputedStyle(marquee).getPropertyValue('--marquee-gap'));

  const measure = document.createElement('span');
  measure.className = segmentClass;
  measure.setAttribute('aria-hidden', 'true');
  measure.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;white-space:nowrap;';
  applyAnnouncementTextStyles(measure, textStyles);
  measure.innerHTML = segmentHTML;
  marquee.appendChild(measure);

  const segmentWidth = measure.offsetWidth;
  marquee.removeChild(measure);

  const unitWidth = segmentWidth + gapPx;
  if (unitWidth <= 0) return;

  const containerWidth = marquee.clientWidth;
  let repeatsPerSet = 1;

  while (repeatsPerSet * unitWidth < containerWidth + unitWidth) {
    repeatsPerSet += 1;
  }

  track.replaceChildren();

  const totalSegments = repeatsPerSet * 2;
  for (let index = 0; index < totalSegments; index += 1) {
    const segment = document.createElement('span');
    segment.className = segmentClass;
    applyAnnouncementTextStyles(segment, textStyles);
    segment.innerHTML = segmentHTML;
    if (index > 0) segment.setAttribute('aria-hidden', 'true');
    track.appendChild(segment);
  }

  track.style.setProperty('--marquee-shift', `${repeatsPerSet * unitWidth}px`);
  marquee.setAttribute('data-ready', '');
}

function initAnnouncementMarquees() {
  document.querySelectorAll('.announcement-marquee').forEach((marquee) => {
    if (!(marquee instanceof HTMLElement)) return;
    marquee.removeAttribute('data-ready');
    buildAnnouncementMarquee(marquee);
  });
}

const debouncedInit = debounce(initAnnouncementMarquees, RESIZE_DEBOUNCE_MS);

function scheduleInit() {
  initAnnouncementMarquees();

  if (document.fonts?.ready) {
    document.fonts.ready.then(initAnnouncementMarquees);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleInit);
} else {
  scheduleInit();
}

document.addEventListener('shopify:section:load', scheduleInit);
window.addEventListener('resize', debouncedInit);
